import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCountry } from "@/lib/CountryContext";

/**
 * Runtime SEO patcher — keeps `<html lang>` and `<link rel="canonical">`
 * aligned with the visitor's current country + language. Mount once at
 * the app root; it doesn't render anything.
 *
 * Static hreflang alternates are in `public/index.html` — they don't
 * change per session, so we hand-code them there for crawlers that don't
 * execute JS.
 */
export default function SEOHead() {
    const { country } = useCountry();
    const { i18n } = useTranslation();

    useEffect(() => {
        // <html lang="...">
        document.documentElement.lang = i18n.resolvedLanguage || i18n.language || "en";

        // Canonical URL — swap the leading /XX segment with the active
        // country so search engines index one URL per market.
        const url = new URL(window.location.href);
        const parts = url.pathname.split("/").filter(Boolean);
        const knownCodes = ["HK", "AU", "SG", "UK", "NZ", "US"];
        if (parts[0] && knownCodes.includes(parts[0].toUpperCase())) {
            parts[0] = country.toLowerCase();
        }
        const canonicalPath = "/" + parts.join("/");
        const canonicalUrl = `${url.origin}${canonicalPath}`;
        let link = document.querySelector('link[rel="canonical"]');
        if (!link) {
            link = document.createElement("link");
            link.rel = "canonical";
            document.head.appendChild(link);
        }
        link.href = canonicalUrl;
    }, [country, i18n.resolvedLanguage, i18n.language]);

    return null;
}
